/*
 * The booking calendars' grey "Reserving your slot…" screen (booking-calendar-inline, -clinic and -gp).
 *
 * A booking runs several requests one after another (reserve the slot, add the appointment, save its details,
 * then the basket page). The screen goes up the moment a booking starts (Continue or Skip in the phone pop-up,
 * or the press when no pop-up is needed) and stays until the basket opens, so the wait reads as progress and
 * nothing behind it can be pressed twice. Each calendar takes it down on a failure, before its error shows.
 *
 * BookingBusy.show(text) / say(text) / hide() / shown() on the page's window; create(win) for tests.
 * A safety timeout takes it down after TIMEOUT_MS from the last show or say (longer than Book and add's own
 * 20s wait for the basket, which starts at its say), and coming back from the basket out of the page cache
 * never shows it. When it goes, focus goes back to what had it.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && root.document && !root.BookingBusy) {
    var own = api.create(root);
    root.BookingBusy = {
      RESERVING: api.RESERVING, ADDING: api.ADDING,
      show: own.show, say: own.say, hide: own.hide, shown: own.shown,
    };
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var RESERVING = 'Reserving your slot…';
  var ADDING = 'Adding to your basket…';
  var TIMEOUT_MS = 30000;
  var HELD_KEYS = { Enter: 1, ' ': 1, Tab: 1 };

  // A white card on the grey: dark text reads on any page behind it (white text on the grey did not).
  var CSS =
    '.bw-busy{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;' +
    'padding:16px;background:rgba(0,0,0,.45);outline:none;animation:bw-busy-in .12s ease-out;' +
    '-webkit-tap-highlight-color:transparent;touch-action:none}' +
    '.bw-busy__card{display:flex;flex-direction:column;align-items:center;gap:14px;max-width:320px;' +
    'padding:24px 28px;border-radius:14px;background:#fff;color:#1a1a1a;box-shadow:0 10px 30px rgba(0,0,0,.25)}' +
    '.bw-busy__spin{width:40px;height:40px;border-radius:50%;border:4px solid #d6e7f3;' +
    'border-top-color:var(--odt-blue,#006EB3);animation:bw-busy-spin .8s linear infinite}' +
    '.bw-busy__text{margin:0;font-family:"Montserrat",system-ui,sans-serif;font-weight:800;font-size:17px;' +
    'line-height:1.3;text-align:center}' +
    '@keyframes bw-busy-spin{to{transform:rotate(360deg)}}' +
    '@keyframes bw-busy-in{from{opacity:0}to{opacity:1}}' +
    '@media (prefers-reduced-motion:reduce){.bw-busy{animation:none}.bw-busy__spin{animation-duration:2.4s}}';

  function create(win) {
    var doc = win.document;
    var screen = null;
    var textEl = null;
    var timer = null;
    var wanted = '';      // the text to show; written on the next tick for a new screen (see show)
    var filled = false;
    var before = null;    // what had focus, to put it back when the screen goes

    function styles() {
      if (doc.getElementById('bw-busy-style') || !doc.head) return;
      var s = doc.createElement('style');
      s.setAttribute('id', 'bw-busy-style');
      s.textContent = CSS;
      doc.head.appendChild(s);
    }

    function build() {
      var s = doc.createElement('div');
      s.className = 'bw-busy';
      s.setAttribute('role', 'status');
      s.setAttribute('aria-live', 'polite');
      s.setAttribute('tabindex', '-1');
      var spin = doc.createElement('span');
      spin.className = 'bw-busy__spin';
      spin.setAttribute('aria-hidden', 'true');
      var p = doc.createElement('p');
      p.className = 'bw-busy__text';
      var card = doc.createElement('div');
      card.className = 'bw-busy__card';
      card.appendChild(spin);
      card.appendChild(p);
      s.appendChild(card);
      textEl = p;
      return s;
    }

    // Focus sits on the screen, but a key could still press the button behind it or tab to it.
    function holdKeys(e) {
      if (screen && e && HELD_KEYS[e.key]) { e.preventDefault(); e.stopPropagation(); }
    }

    function write() {
      if (screen && filled) textEl.textContent = wanted;
    }

    // Restarted on every show and say: once the slot is held, Book and add's own wait for the basket (20s)
    // starts afresh, however long the booking took.
    function restartTimer() {
      if (timer) win.clearTimeout(timer);
      timer = win.setTimeout(hide, TIMEOUT_MS);
    }

    function show(text) {
      if (!doc.body) return;
      styles();
      wanted = String(text || RESERVING);
      if (!screen) {
        before = doc.activeElement || null;
        screen = build();
        filled = false;
        doc.body.appendChild(screen);
        doc.addEventListener('keydown', holdKeys, true);
        try { screen.focus({ preventScroll: true }); } catch (e) { /* focus only */ }
        // A live region filled in the same task it goes on the page is often not announced (VoiceOver):
        // the text goes in on the next tick.
        var mine = screen;
        win.setTimeout(function () { if (screen === mine) { filled = true; write(); } }, 0);
      }
      write();
      restartTimer();
    }

    function say(text) {
      if (!screen) return;
      wanted = String(text || '');
      write();
      restartTimer();
    }

    function hide() {
      if (timer) { win.clearTimeout(timer); timer = null; }
      if (!screen) return;
      var had = doc.activeElement === screen;
      doc.removeEventListener('keydown', holdKeys, true);
      if (screen.parentNode) screen.parentNode.removeChild(screen);
      screen = null;
      textEl = null;
      // Back where they were (the button), unless something else took focus meanwhile.
      if (had && before && before.focus) { try { before.focus({ preventScroll: true }); } catch (e) { /* gone */ } }
      before = null;
    }

    win.addEventListener('pageshow', function (e) { if (e && e.persisted) hide(); });

    return { show: show, say: say, hide: hide, shown: function () { return !!screen; } };
  }

  return { RESERVING: RESERVING, ADDING: ADDING, TIMEOUT_MS: TIMEOUT_MS, create: create };
});
